import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Container,
} from "@mui/material";
import { styled } from "@mui/system";

interface TabPanelProps {
  children?: React.ReactNode;
  value: string;
  index: string;
}

type TabContentType = {
  mission: { text: string; title: string };
  vision: { text: string; title: string };
  value: { text: string; title: string };
};

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: "bold",
  minWidth: "auto",
  padding: "6px 16px",
  borderRadius: "20px",
  marginRight: "8px",
  color:"black",
  '&.Mui-selected': {
    color: "#f97316", // Active tab color
  },
}));


const StyledTabs = styled(Tabs)({
  "& .MuiTabs-indicator": {
    display: "none",
  },
  marginBottom: "20px",
  color:"#f97316"

  
});

const ImageWrapper = styled(Box)({
  position: "relative",
  height: "400px",
  width: "100%",
  display: "flex",
  justifyContent: "center",
});

const TopImage = styled("img")(({ theme }) => ({
  position: "absolute",
  width: "45%",
  height: "auto",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  objectFit: "cover",
  top: "10%", // Default for small screens
  left: "10%",
  zIndex: 2,
  [theme.breakpoints.up("lg")]: {
    top: "20%", // Adjust for large screens
  },
}));

const BottomImage = styled("img")(({ theme }) => ({
  position: "absolute",
  width: "45%",
  height: "auto",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  objectFit: "cover",
  bottom: "10%", // Default for small screens
  right: "10%",
  zIndex: 1,
  [theme.breakpoints.up("lg")]: {
    bottom: "0%", // Adjust for large screens
  },
}));

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const LandingAbout = () => {
  const [selectedTab, setSelectedTab] = useState<keyof TabContentType>("mission");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const tabContent: TabContentType = {
    mission: {
      text: "Our mission is to provide high-quality, plagiarism-free assignment writing services that help students succeed academically. We aim to assist students in achieving their academic goals by offering personalized writing support for essays, research papers, dissertations, and more.",
      title: "We help students achieve academic excellence.",
    },
    vision: {
      text: "Our vision is to become the leading provider of assignment writing services globally, recognized for our commitment to delivering high-quality, timely, and reliable academic solutions. We envision empowering students to excel in their studies with the support of professional writing experts.",
      title: "Empowering students with expert academic writing services.",
    },
    value: {
      text: "Our values revolve around delivering exceptional value to students, ensuring the highest standards of academic integrity, and fostering a culture of trust and transparency. We prioritize customer satisfaction, innovation, and continuous improvement in all aspects of our assignment writing services.",
      title: "Reliable, Trustworthy, and Academic Integrity.",
    },
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: keyof TabContentType) => {
    setSelectedTab(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{backgroundColor:"#f3f4f6"}}>
      <Box id="about" sx={{ py: { xs: 4, md: 8 } }} >
        <Grid container spacing={4}>
          {/* Left Section */}
          <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }} className="component">
            <Box sx={{ mb: 4 }} >
              <Typography
                variant="overline"
                sx={{
                  color: "#f97316",
                  fontWeight:"bold",
                  letterSpacing: 2,
                  display: "block",
                  mb: 2,
                }}
              >
                {selectedTab.toUpperCase()}
              </Typography>
              <Typography
  variant="h3"
  sx={{
    fontWeight: "bold",
    color: "#172554",
    mb: 2,
    fontSize: { xs: "2rem", md: "2.5rem" },
    // textAlign: "center", // Center-align the text
  }}
>
  {tabContent[selectedTab].title.split(" ").map((word, index, words) =>
    index === 2 ? ( // Highlight starting from the 3rd word
      <span
        key={index}
        style={{
          background:
            "linear-gradient(-96deg, rgb(194 183 183 / 0%) 0%, rgb(255 249 220 / 4%) 28%, rgb(255, 162, 49) 100%, rgb(255 255 255 / 0%) 100%)",
          padding: "0px 4px",
          borderLeft: "2px solid rgba(253,150,16,1)",
          display: "inline-block",
        }}
      >
        {words.slice(index, index + 3).join(" ")} {/* Combine 2 consecutive words */}
      </span>
    ) : index === 3 ? null : ( // Skip the second word of the highlighted pair
      <span key={index}>{word} </span>
    )
  )}
</Typography>


              <Typography
                variant="body1"
                sx={{
                  color: "black",
                  mb: 4,
                  fontSize: "1.1rem",
                  lineHeight: 1.6,
                }}
              >
                {tabContent[selectedTab].text}
              </Typography>
            </Box>

            <StyledTabs value={selectedTab} onChange={handleTabChange}>
  <StyledTab
    value="mission"
    label="Mission"
    // Remove the isActive prop here to avoid passing it to the DOM
  />
  <StyledTab
    value="vision"
    label="Vision"
    // Remove the isActive prop here
  />
  <StyledTab
    value="value"
    label="Values"
    // Remove the isActive prop here
  />
</StyledTabs>

            {selectedTab === "mission" && (
              <Box sx={{ mt: 4 }}>
                {["High-quality assignment writing services", "Plagiarism-free content for students", "Expert writers supporting academic success"].map(
                  (item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                        gap: 2,
                        
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "black" }}
                      >
                        0{index + 1}.
                      </Typography>
                      <Typography variant="body1" sx={{ color: "black" }}>
                        {item}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            )}

            {selectedTab === "vision" && (
              <Box sx={{ mt: 4 }}>
                {["Become the global leader in academic writing services", "Offer innovative solutions to students worldwide", "Provide expert academic assistance for every student"].map(
                  (item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "black" }}
                      >
                        0{index + 1}.
                      </Typography>
                      <Typography variant="body1" sx={{ color: "black" }}>
                        {item}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            )}

            {selectedTab === "value" && (
              <Box sx={{ mt: 4 }}>
                {["Commitment to academic integrity", "Customer satisfaction as our priority", "Innovation in providing academic solutions"].map(
                  (item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "black" }}
                      >
                        0{index + 1}.
                      </Typography>
                      <Typography variant="body1" sx={{ color: "black" }}>
                        {item}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            )}
          </Grid>

          {/* Right Section */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              order: { xs: 2, md: 1 },
              display: "flex",
              justifyContent: "center",
            }}
            className="component"
          >
            <ImageWrapper>
              <TopImage src="team4.jpg" alt="Office Space" />
              <BottomImage src="team2.jpg" alt="Team Member" />
            </ImageWrapper>
          </Grid>
        </Grid>
      </Box>

    
     
    </Container>
  );
};

export default LandingAbout;
